import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { nextProjectSelection, projectPillText, projectSheetButtonText, tickedCodes } from "./projectSelection.js";

/**
 * The phone shell (mockups/cockpit-v3/mobile/Main.dc.html and Projects.dc.html):
 * a bottom tab bar (Today, Plan, NCRs, Report) with the existing nav counts in
 * place of the desktop top bar, and the project pill that opens a bottom sheet
 * with the existing multi-select. App.jsx provides the shell's data through
 * PhoneShellContext; each phone screen draws its own header with
 * <PhoneTopRow /> (the NEG mark and the project pill) so the header stays one
 * landmark, as in the mockups.
 */
export const PhoneShellContext = createContext(null);

const TABS = [
  { key: "today", label: "Today", countKey: "today", icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></> },
  { key: "plan", label: "Plan", countKey: null, icon: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></> },
  {
    key: "ncrs",
    label: "NCRs",
    countKey: "ncrs",
    icon: <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />,
  },
  { key: "weekly", label: "Report", countKey: "reports", icon: <><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></> },
];

export function Svg({ children, size = 22, stroke = 2, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} aria-hidden="true" {...rest}>
      {children}
    </svg>
  );
}

export const ICON = {
  back: <path d="M15 6l-6 6 6 6" />,
  prev: <path d="M15 6l-6 6 6 6" />,
  next: <path d="M9 6l6 6-6 6" />,
  chevDown: <path d="M6 9l6 6 6-6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  download: <path d="M12 4v12M7 11l5 5 5-5M5 20h14" />,
  swap: <path d="M5 9l-3 3 3 3M19 9l3 3-3 3M2 12h20" />,
  warn: <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />,
  checkCircle: <><circle cx="12" cy="12" r="9" /><path d="M8 12l3 3 5-6" /></>,
  lock: <><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></>,
};

/** Bottom tab bar. Counts are the same nav counts the desktop top bar shows (shown only when above zero). */
export function PhoneTabBar() {
  const shell = useContext(PhoneShellContext);
  if (!shell) return null;
  const { screen, onNavigate, navCounts = {} } = shell;
  return (
    <nav className="ph-tabbar" aria-label="Sections">
      {TABS.map((tab) => {
        const count = tab.countKey ? navCounts[tab.countKey] : 0;
        const on = screen === tab.key;
        return (
          <a
            key={tab.key}
            href={`#${tab.key}`}
            className={`ph-tab${on ? " on" : ""}`}
            aria-current={on ? "page" : undefined}
            data-testid={`nav-${tab.key}`}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(tab.key);
            }}
          >
            <Svg>{tab.icon}</Svg>
            {tab.label}
            {count ? ` · ${count}` : ""}
          </a>
        );
      })}
    </nav>
  );
}

/** The NEG mark and the project pill (opens the "Projects shown" sheet). Renders nothing outside the phone shell. */
export function PhoneTopRow() {
  const shell = useContext(PhoneShellContext);
  if (!shell) return null;
  const { projectCodes, selectedCodes, projectsOpen, openProjects } = shell;
  return (
    <div className="ph-toprow">
      <span className="ph-neg">NEG</span>
      <button type="button" className="ph-pill" aria-haspopup="dialog" aria-expanded={Boolean(projectsOpen)} data-testid="ph-project-pill" onClick={openProjects}>
        <span className="ph-pilltext">{projectPillText(selectedCodes, projectCodes)}</span>
        <Svg size={14}>{ICON.chevDown}</Svg>
      </button>
    </div>
  );
}

/**
 * Provides the shell's data to the phone screens and draws what belongs to the
 * shell rather than to a screen: the tab bar and the "Projects shown" sheet
 * (both siblings of the screen, so a sheet never sits inside a screen header).
 * With no `value` (desktop width) it is transparent.
 */
export function PhoneShellProvider({ value, children }) {
  const [projectsOpen, setProjectsOpen] = useState(false);
  const ctx = value ? { ...value, projectsOpen, openProjects: () => setProjectsOpen(true) } : null;
  return (
    <PhoneShellContext.Provider value={ctx}>
      {children}
      {ctx ? <PhoneTabBar /> : null}
      {ctx && projectsOpen ? (
        <ProjectsSheet projectCodes={ctx.projectCodes} selectedCodes={ctx.selectedCodes} onSelectCodes={ctx.onSelectCodes} identity={ctx.identity} onClose={() => setProjectsOpen(false)} />
      ) : null}
    </PhoneShellContext.Provider>
  );
}

/** Projects.dc.html: the existing multi-select as checkboxes in a bottom sheet. */
export function ProjectsSheet({ projectCodes, selectedCodes, onSelectCodes, identity, onClose }) {
  const ticked = tickedCodes(selectedCodes, projectCodes);
  return (
    <PhoneSheet labelledBy="ph-projects-title" onClose={onClose} testId="ph-projects-sheet">
      <h2 id="ph-projects-title" className="ph-sheet-title">Projects shown</h2>
      <p className="ph-sheet-note">Today, Plan, NCRs and Weekly report show only the projects you tick.</p>
      <div className="ph-checklist">
        {projectCodes.map((code) => (
          <label key={code} className="ph-checkrow">
            <input type="checkbox" checked={ticked.includes(code)} onChange={() => onSelectCodes(nextProjectSelection(selectedCodes, projectCodes, code))} />
            <span className="ph-mono ph-strong">{code}</span>
          </label>
        ))}
      </div>
      <button type="button" className="ph-btn pri" onClick={onClose}>{projectSheetButtonText(selectedCodes, projectCodes)}</button>
      {identity ? <span className="ph-foot-note">Signed in as {String(identity.role || "site manager").toLowerCase()}</span> : null}
    </PhoneSheet>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * While an overlay (a sheet or a full-screen view) is open, everything beneath
 * it - the screen, the tab bar, any overlay under it - is inert and hidden from
 * assistive technology, and comes back exactly as it was when the overlay closes.
 */
function useBackgroundInert(ref) {
  useEffect(() => {
    const node = ref.current;
    const overlay = node && node.closest(".ph-overlay, .ph-full");
    if (!overlay) return undefined;
    const saved = [];
    // Everything beneath: the siblings before the overlay (the screen's header and body, any overlay under it) and the tab bar.
    const beneath = [];
    for (let el = overlay.previousElementSibling; el; el = el.previousElementSibling) beneath.push(el);
    const root = overlay.closest(".ph-root");
    const tabbar = root ? root.querySelector(":scope > .ph-tabbar") : null;
    if (tabbar && !beneath.includes(tabbar) && !tabbar.contains(overlay)) beneath.push(tabbar);
    for (const el of beneath) {
      saved.push({ el, inert: el.hasAttribute("inert"), hidden: el.getAttribute("aria-hidden") });
      el.setAttribute("inert", "");
      el.setAttribute("aria-hidden", "true");
    }
    return () => {
      for (const { el, inert, hidden } of saved) {
        if (!inert) el.removeAttribute("inert");
        if (hidden === null) el.removeAttribute("aria-hidden");
        else el.setAttribute("aria-hidden", hidden);
      }
    };
  }, [ref]);
}

/**
 * A bottom sheet (role="dialog", aria-modal): the dimmed page behind it, a
 * grab handle, focus moves in on open, Escape and a tap on the dimmed page
 * close it, and focus returns to what opened it.
 */
export function PhoneSheet({ labelledBy, onClose, children, testId }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useBackgroundInert(ref);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const previous = document.activeElement;
    const first = node.querySelector("[data-autofocus]") || node.querySelector(FOCUSABLE) || node;
    first.focus({ preventScroll: true });
    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, []);
  return (
    <div className="ph-overlay">
      <div className="ph-backdrop" aria-hidden="true" onClick={() => closeRef.current()} />
      <section ref={ref} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} className="ph-sheet" data-testid={testId}>
        <span aria-hidden="true" className="ph-grab" />
        {children}
      </section>
    </div>
  );
}

/**
 * A full-screen view over the tab screens (PlanItem, PlanBook, NcrDetail ...):
 * a header with a back control, a scrolling body and an optional footer. It
 * covers the shell, as the mockups' sub-screens have no tab bar.
 */
export function PhoneFullScreen({ title, subtitle, onBack, backLabel = "Back", cancelStyle = false, titleId, headerRight = null, children, footer = null, footerTwo = false, testId }) {
  const ref = useRef(null);
  useBackgroundInert(ref);
  return (
    <div className="ph-full" ref={ref} data-testid={testId} role="region" aria-labelledby={titleId}>
      <header className="ph-fullhead">
        {cancelStyle ? (
          <button type="button" className="ph-textbtn" onClick={onBack}>{backLabel}</button>
        ) : (
          <button type="button" className="ph-iconbtn plain" aria-label={backLabel} onClick={onBack}>
            <Svg>{ICON.back}</Svg>
          </button>
        )}
        <span className="ph-fulltitle">
          <span id={titleId} className="ph-fulltitle-main">{title}</span>
          {subtitle ? <span className="ph-fulltitle-sub">{subtitle}</span> : null}
        </span>
        {headerRight}
        {cancelStyle && !headerRight ? <span className="ph-fullspacer" /> : null}
      </header>
      <main className="ph-fullmain">{children}</main>
      {footer ? <footer className={`ph-fullfoot${footerTwo ? " two" : ""}`}>{footer}</footer> : null}
    </div>
  );
}

/** A small mono status chip: tone is "warn" | "ok" | "acc" | "bad" | "mute". */
export function PhoneChip({ tone = "mute", children, ...rest }) {
  return <span className={`ph-chip ${tone}`} {...rest}>{children}</span>;
}
